import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Avatar,
  Checkbox,
  MenuItem,
  Paper,
  Popper,
} from '@mui/material'
import { styled } from '@mui/system'
import InputBase from '@mui/material/InputBase'
import {
  Column,
  GridApi,
  RowNode,
  ValueFormatterParams,
} from 'ag-grid-community'
import { RowData } from '../../../../../../BulkSheet/RowDataManager/rowDataManager'
import { FunctionProperty } from '../../../../../../../../lib/commons/appFunction'
import repositories from '../../../../../../meta/repositories'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import _ from 'lodash'
import CancelButton from '../../../../../../../components/buttons/CancelButton'
import SaveButton from '../../../../../../../components/buttons/SaveButton'

const RootDiv = styled('div')({
  width: '100%',
  height: '100%',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
})
const StyledMenuItem = styled(MenuItem)({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  '& .MuiCheckbox-root': { marginRight: '0px !important' },
  '& .MuiAvatar-root': { margin: '0px 6px 0px 0px !important' },
})
const CheckboxLabel = styled('span')({
  whiteSpace: 'nowrap',
})
const StyledPaper = styled(Paper)({
  '& .MuiAutocomplete-option': { paddingLeft: '1px' },
})
const ButtonGroup = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '36px',
})
const StyledAvatar = styled(Avatar)({
  marginRight: '5px',
  height: '23px',
  width: '23px',
  borderRadius: '50%',
})

const iconDefined = ['ProjectMember', 'User']

interface AutocompleteOption {
  uuid: string
  name: string
  iconUrl?: string
}

interface Props {
  value: AutocompleteOption[]
  node: RowNode
  api: GridApi
  gridApi: GridApi
  eventKey: string | null
  rowIndex: number
  column: Column
  data: RowData

  field: string
  uiMeta: FunctionProperty
  onValueChange: (value: any) => void
}

export const multiAutocompleteCellFilterValueFormatter = (
  params: ValueFormatterParams
) => {
  const value = params.value
  if (!value || !value.trim()) {
    return '(Blanks)'
  }
  return value
}

const MultiAutocompleteCellEditor = (props: Props) => {
  const [value, setValue] = useState<AutocompleteOption[] | undefined>(
    props.value
  )
  const [text, setText] = useState(() =>
    props.eventKey?.length === 1 ? props.eventKey : ''
  )
  const [suggestions, setSuggestions] = useState<AutocompleteOption[]>([])
  const [open, setOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const listboxRef = useRef<HTMLUListElement | null>(null)

  // debounce: ref 経由で最新の props/state にアクセス
  const setOptionsRef = useRef<(searchText: string) => Promise<void>>()
  setOptionsRef.current = async (searchText: string) => {
    const cellEditorParams = props.column.getColDef().cellEditorParams
    const searchOptions = props.uiMeta.searchOptions.build(props.data)
    let options: AutocompleteOption[] = await repositories[
      props.uiMeta.referenceEntity!
    ].search(searchText, searchOptions, props.uiMeta)
    const selectOptionsFilter = cellEditorParams.selectOptionsFilter
    options = selectOptionsFilter ? selectOptionsFilter(options) : options
    setSuggestions(options)
    setOpen(options.length > 0)
  }

  const search = useMemo(
    () => _.debounce((v: string) => setOptionsRef.current?.(v), 300),
    []
  )

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。セルエディタの初期化処理
  }, [])

  useEffect(() => {
    const input = inputRef.current
    if (!input) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
      }
    }
    input.addEventListener('keydown', handler)
    return () => input.removeEventListener('keydown', handler)
  }, [])

  const getIcon = (
    referenceEntity: string | undefined,
    iconUrl: string | undefined
  ) => {
    if (!referenceEntity || !iconDefined.includes(referenceEntity)) {
      return <></>
    }
    return <StyledAvatar variant="circular" src={iconUrl} />
  }

  const handleFocus = () => {
    search(text ? text : '')
  }

  const handleInputChange = (
    _event: React.ChangeEvent<{}>,
    newValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'input') {
      setText(newValue)
      search(newValue)
    }
  }

  const handleValueChanged = (
    _event: React.ChangeEvent<{}>,
    newOptions: AutocompleteOption[] | null,
    _reason: AutocompleteChangeReason
  ) => {
    const focusedOption = listboxRef.current?.querySelector('.Mui-focused')
    const focusedIndex = focusedOption?.getAttribute('data-option-index')

    const newValue = newOptions || []
    props.onValueChange(newValue)
    setValue(newValue)

    requestAnimationFrame(() => {
      if (listboxRef.current && focusedIndex != null) {
        const target = listboxRef.current.querySelector(
          `[data-option-index="${focusedIndex}"]`
        )
        target?.dispatchEvent(new MouseEvent('mousemove', { bubbles: true }))
      }
    })
  }

  const handleSubmit = () => {
    setOpen(false)
    props.api.stopEditing()
  }

  const handleCancel = () => {
    setOpen(false)
    props.api.stopEditing(true)
  }

  const handleClose = (_e: React.SyntheticEvent, reason: string) => {
    if (['blur', 'toggleInput'].includes(reason)) {
      return
    }
    let cancel = false
    if (reason === 'escape') {
      cancel = true
    }
    setOpen(false)
    props.api.stopEditing(cancel)
  }

  const { uiMeta } = props
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
  const checkedIcon = <CheckBoxIcon fontSize="small" />

  return (
    <RootDiv>
      <Autocomplete
        multiple={true}
        options={suggestions}
        getOptionLabel={option => (option.name ? option.name : '')}
        renderOption={(
          renderProps,
          option: AutocompleteOption,
          { selected }
        ) => {
          return (
            <StyledMenuItem {...renderProps}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
                color={'primary'}
              />
              {getIcon(uiMeta.referenceEntity, option.iconUrl)}
              <CheckboxLabel>
                {repositories[uiMeta.referenceEntity!].getLabel(option)}
              </CheckboxLabel>
            </StyledMenuItem>
          )
        }}
        isOptionEqualToValue={(optionValue, option) => {
          return optionValue.uuid === option.uuid
        }}
        filterOptions={(options: AutocompleteOption[]) => {
          return options
        }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <InputBase
            inputRef={inputRef}
            ref={params.InputProps.ref}
            inputProps={params.inputProps}
            autoFocus={true}
          />
        )}
        PopperComponent={popperProps => (
          <Popper {...popperProps} placement="bottom-start" />
        )}
        PaperComponent={paperProps => (
          <StyledPaper {...paperProps}>
            {paperProps.children}
            <ButtonGroup>
              <SaveButton onClick={handleSubmit} />
              <CancelButton onClick={handleCancel} />
            </ButtonGroup>
          </StyledPaper>
        )}
        autoSelect={false}
        blurOnSelect={false}
        open={open}
        noOptionsText={''}
        value={value}
        onFocus={handleFocus}
        onInputChange={handleInputChange}
        onChange={handleValueChanged}
        onClose={handleClose}
        disableCloseOnSelect={true}
        ListboxProps={{
          ref: listboxRef,
        }}
      />
    </RootDiv>
  )
}

export default MultiAutocompleteCellEditor
